import { useMutation } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, FileUp, Search, Wand2 } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { ConfidenceChip } from '@/components/ai-accountant/confidence-chip';
import { LineEditor, useInvalidateBanking } from '@/components/banking/line-editor';
import { ErrorBanner } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { api } from '@/lib/api';
import {
  AI_ACCEPT_CONFIDENCE,
  useBankAccounts,
  useBankLines,
  type AiSuggestion,
  type BankAccount,
  type LineStatus,
  type StatementLine,
} from '@/lib/banking';
import { useMe } from '@/lib/clients';
import { displayDate } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import { cn } from '@/lib/utils';

interface Search {
  accountId?: string;
  tab?: LineStatus;
}

export const Route = createFileRoute('/banking/')({
  component: BankTransactionsPage,
  validateSearch: (s: Record<string, unknown>): Search => ({
    ...(typeof s['accountId'] === 'string' ? { accountId: s['accountId'] } : {}),
    ...(s['tab'] === 'for_review' || s['tab'] === 'categorised' || s['tab'] === 'excluded'
      ? { tab: s['tab'] }
      : {}),
  }),
});

const TABS: Array<{ key: LineStatus; label: string }> = [
  { key: 'for_review', label: 'For review' },
  { key: 'categorised', label: 'Categorised' },
  { key: 'excluded', label: 'Excluded' },
];

/** Screen B1 (document 06): Bank transactions. */
function BankTransactionsPage() {
  const orgId = useOrgId();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const banks = useBankAccounts();
  const me = useMe();
  const canEdit = me.data?.isStaff ?? false;
  // the account with most to review, unless one was chosen
  const selected =
    banks.data?.find((b) => b.accountId === search.accountId) ??
    [...(banks.data ?? [])].sort((a, b) => b.forReview - a.forReview)[0] ??
    null;
  const tab = search.tab ?? 'for_review';

  return (
    <>
      <PageHeader
        title="Bank transactions"
        actions={
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  More <ChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to="/banking/rules">Manage rules</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/banking/reconcile">Reconcile</Link>
                </DropdownMenuItem>
                {canEdit ? (
                  <DropdownMenuItem asChild>
                    <Link to="/banking/transfers/new">Record a transfer</Link>
                  </DropdownMenuItem>
                ) : null}
                {selected ? (
                  <DropdownMenuItem asChild>
                    <Link
                      to="/accounting/chart-of-accounts/$accountId/register"
                      params={{ accountId: selected.accountId }}
                    >
                      Go to bank register
                    </Link>
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
            {canEdit ? (
              <Button asChild>
                <Link
                  to="/banking/import"
                  search={selected ? { accountId: selected.accountId } : {}}
                >
                  <FileUp className="size-4" /> Import statement
                </Link>
              </Button>
            ) : null}
          </>
        }
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : banks.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : banks.isError ? (
        <EmptyState title="Could not load bank accounts" hint={banks.error.message} />
      ) : !selected ? (
        <EmptyState
          title="No bank accounts yet"
          hint="Add a Bank or Credit card account to the chart of accounts, then import its statement."
          action={
            <Button asChild>
              <Link to="/accounting/chart-of-accounts">Chart of accounts</Link>
            </Button>
          }
        />
      ) : (
        <>
          <BankTiles
            banks={banks.data}
            selected={selected.accountId}
            onSelect={(accountId) => void navigate({ search: { accountId, tab } })}
          />
          <nav className="mb-3 flex gap-6 border-b text-sm" aria-label="Bank transaction tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                aria-current={tab === t.key ? 'page' : undefined}
                className={cn(
                  '-mb-px border-b-2 px-1 pb-2 font-medium',
                  tab === t.key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
                onClick={() =>
                  void navigate({ search: { accountId: selected.accountId, tab: t.key } })
                }
              >
                {t.label}
                {t.key === 'for_review' ? ` (${selected.forReview})` : ''}
              </button>
            ))}
          </nav>
          <LinesTable
            key={`${selected.accountId}-${tab}`}
            bank={selected}
            banks={banks.data}
            tab={tab}
            canEdit={canEdit}
          />
        </>
      )}
    </>
  );
}

function BankTiles({
  banks,
  selected,
  onSelect,
}: {
  banks: BankAccount[];
  selected: string;
  onSelect: (accountId: string) => void;
}) {
  return (
    <div className="mb-5 flex gap-3 overflow-x-auto pb-1" role="tablist" aria-label="Bank accounts">
      {banks.map((b) => (
        <button
          key={b.accountId}
          type="button"
          role="tab"
          aria-selected={b.accountId === selected}
          onClick={() => onSelect(b.accountId)}
          className={cn(
            'flex w-[260px] shrink-0 flex-col gap-2 rounded-lg border bg-card p-4 text-left text-sm',
            b.accountId === selected
              ? 'border-primary ring-2 ring-primary/20'
              : 'hover:border-foreground/30',
          )}
        >
          <div>
            <div className="truncate font-semibold">{b.name}</div>
            <div className="truncate text-xs text-muted-foreground">
              {b.bankName ?? b.detailTypeName}
              {b.accountNumber ? ` · ${b.accountNumber}` : ''}
            </div>
          </div>
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs text-muted-foreground">Bank balance</span>
            <span className="tnum font-medium">
              {b.bankBalance === null ? '—' : `${b.currency} ${formatAmount(b.bankBalance)}`}
            </span>
          </div>
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs text-muted-foreground">In Yedo Books</span>
            <span className="tnum font-medium">
              {b.currency} {formatAmount(b.ledgerBalance)}
            </span>
          </div>
          {b.currency !== b.homeCurrency ? (
            <div className="flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
              <span>In {b.homeCurrency}</span>
              <span className="tnum" data-testid="home-ledger-balance">
                {b.homeCurrency} {formatAmount(b.homeLedgerBalance)}
              </span>
            </div>
          ) : null}
          <div className="flex items-center justify-between text-xs">
            <span
              className={cn(b.forReview ? 'font-medium text-primary' : 'text-muted-foreground')}
            >
              {b.forReview} to review
            </span>
            <span className="text-muted-foreground">
              {b.lastImportAt
                ? `Imported ${displayDate(b.lastImportAt.slice(0, 10))}`
                : 'No statement yet'}
            </span>
          </div>
        </button>
      ))}
    </div>
  );
}

function LinesTable({
  bank,
  banks,
  tab,
  canEdit,
}: {
  bank: BankAccount;
  banks: BankAccount[];
  tab: LineStatus;
  canEdit: boolean;
}) {
  const orgId = useOrgId();
  const invalidate = useInvalidateBanking();
  const lines = useBankLines(bank.accountId, tab);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const needle = q.trim().toLowerCase();
  const rows = useMemo(
    () =>
      (lines.data ?? []).filter(
        (l) =>
          !needle ||
          [l.description, l.payee, l.reference, l.contactName, l.categoryLabel, l.memo]
            .filter(Boolean)
            .some((v) => v!.toLowerCase().includes(needle)) ||
          (l.spent ?? l.received ?? '').includes(needle.replace(/,/g, '')),
      ),
    [lines.data, needle],
  );

  const batch = useMutation({
    mutationFn: (body: { action: 'accept' | 'exclude' | 'undo'; ids: string[] }) =>
      api<{ done: number; skipped: number; failed: Array<{ id: string; message: string }> }>(
        `/orgs/${orgId}/banking/lines/batch`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: async (r, body) => {
      setChecked(new Set());
      await invalidate();
      const verb =
        body.action === 'accept' ? 'Accepted' : body.action === 'exclude' ? 'Excluded' : 'Undone';
      toast(
        `${verb} ${r.done} ${r.done === 1 ? 'transaction' : 'transactions'}` +
          (r.skipped ? `; ${r.skipped} had nothing to ${body.action}` : '') +
          (r.failed.length ? `; ${r.failed.length} failed: ${r.failed[0]!.message}` : '') +
          '.',
        { tone: r.failed.length ? 'error' : 'success' },
      );
    },
  });
  const single = (action: 'accept' | 'undo', id: string) => batch.mutate({ action, ids: [id] });

  const allChecked = rows.length > 0 && rows.every((r) => checked.has(r.id));
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(rows.map((r) => r.id)));
  const toggle = (id: string) =>
    setChecked((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            aria-label="Search bank transactions"
            placeholder="Search by description, payee or amount"
            className="w-[300px] pl-8"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        {canEdit && checked.size > 0 ? (
          <div className="flex items-center gap-2" data-testid="batch-actions">
            <span className="text-sm text-muted-foreground">{checked.size} selected</span>
            {tab === 'for_review' ? (
              <>
                <Button
                  size="sm"
                  disabled={batch.isPending}
                  onClick={() => batch.mutate({ action: 'accept', ids: [...checked] })}
                >
                  Accept
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={batch.isPending}
                  onClick={() => batch.mutate({ action: 'exclude', ids: [...checked] })}
                >
                  Exclude
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant="outline"
                disabled={batch.isPending}
                onClick={() => batch.mutate({ action: 'undo', ids: [...checked] })}
              >
                Undo
              </Button>
            )}
          </div>
        ) : null}
        {tab === 'for_review' ? (
          <Link
            to="/banking/rules"
            className="ml-auto inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            <Wand2 className="size-4" /> Rules
          </Link>
        ) : null}
      </div>
      <ErrorBanner>{batch.error?.message ?? null}</ErrorBanner>
      {lines.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : lines.isError ? (
        <EmptyState title="Could not load transactions" hint={lines.error.message} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={
            needle
              ? 'No transactions match your search'
              : tab === 'for_review'
                ? 'Nothing to review'
                : tab === 'categorised'
                  ? 'No categorised transactions yet'
                  : 'No excluded transactions'
          }
          {...(tab === 'for_review' && !needle
            ? { hint: 'Import a statement to bring in new bank transactions.' }
            : {})}
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader className="sticky top-0 bg-card">
              <TableRow>
                {canEdit ? (
                  <TableHead className="w-10">
                    <input
                      type="checkbox"
                      className="size-4 accent-primary"
                      aria-label="Select all"
                      checked={allChecked}
                      onChange={toggleAll}
                    />
                  </TableHead>
                ) : null}
                <TableHead className="w-[110px]">Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-[180px]">Payee</TableHead>
                <TableHead className="w-[260px]">
                  {tab === 'for_review'
                    ? 'Category or match'
                    : tab === 'categorised'
                      ? 'Added or matched to'
                      : 'Reason'}
                </TableHead>
                <TableHead className="w-[120px] text-right">Spent</TableHead>
                <TableHead className="w-[120px] text-right">Received</TableHead>
                <TableHead className="w-[150px] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((l) => (
                <Fragment key={l.id}>
                  <TableRow
                    className={cn(
                      tab === 'for_review' && canEdit && 'cursor-pointer',
                      open === l.id && 'bg-accent/40',
                    )}
                    onClick={() => {
                      if (tab === 'for_review' && canEdit) setOpen(open === l.id ? null : l.id);
                    }}
                    data-testid="bank-line"
                  >
                    {canEdit ? (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="size-4 accent-primary"
                          aria-label={`Select ${l.description}`}
                          checked={checked.has(l.id)}
                          onChange={() => toggle(l.id)}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell className="tnum">
                      <span className="inline-flex items-center gap-1">
                        {tab === 'for_review' && canEdit ? (
                          open === l.id ? (
                            <ChevronDown className="size-3.5 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="size-3.5 text-muted-foreground" />
                          )
                        ) : null}
                        {displayDate(l.date)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{l.description}</div>
                      {l.payee && l.payee !== l.description ? (
                        <div className="text-xs text-muted-foreground">{l.payee}</div>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {l.contactName ??
                        (l.suggestion?.kind === 'rule' ? l.suggestion.contactName : '') ??
                        ''}
                    </TableCell>
                    <TableCell>
                      <CategoryCell line={l} tab={tab} />
                    </TableCell>
                    <TableCell className="tnum text-right">{formatAmount(l.spent)}</TableCell>
                    <TableCell className="tnum text-right">{formatAmount(l.received)}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <ActionCell
                        line={l}
                        tab={tab}
                        canEdit={canEdit}
                        busy={batch.isPending}
                        onOpen={() => setOpen(open === l.id ? null : l.id)}
                        onAccept={() => single('accept', l.id)}
                        onUndo={() => single('undo', l.id)}
                      />
                    </TableCell>
                  </TableRow>
                  {open === l.id ? (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={canEdit ? 8 : 7} className="p-0 whitespace-normal">
                        <LineEditor
                          line={l}
                          bank={bank}
                          banks={banks}
                          onDone={() => setOpen(null)}
                        />
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );
}

function CategoryCell({ line, tab }: { line: StatementLine; tab: LineStatus }) {
  if (tab === 'categorised')
    return (
      <div className="flex flex-col gap-0.5">
        <span className="truncate">{line.categoryLabel}</span>
        <span className="flex gap-1">
          {line.ruleName ? (
            <Badge
              variant="secondary"
              title={line.autoAdded ? 'Added automatically by a rule' : 'Accepted from a rule'}
            >
              {line.autoAdded ? 'Auto-added' : 'Rule'}: {line.ruleName}
            </Badge>
          ) : null}
          {line.reconciled ? <Badge variant="outline">Reconciled</Badge> : null}
        </span>
      </div>
    );
  if (tab === 'excluded') return <span className="text-muted-foreground">Excluded</span>;
  const s = line.suggestion;
  if (!s) return <span className="text-muted-foreground">Uncategorised</span>;
  if (s.kind === 'match')
    return (
      <span className="inline-flex flex-col">
        <Badge className="bg-success text-white">1 match found</Badge>
        <span className="mt-0.5 text-xs text-muted-foreground">
          {s.candidate.type} {s.candidate.reference ?? ''} · {displayDate(s.candidate.date)}
        </span>
      </span>
    );
  if (s.kind === 'ai')
    return (
      <span className="inline-flex flex-col gap-0.5" data-testid="ai-suggestion">
        <span className="truncate">
          {s.action === 'transfer' ? `Transfer: ${s.accountName}` : s.accountName}
        </span>
        <span className="inline-flex items-center gap-1">
          <Badge variant="secondary">AI</Badge>
          <ConfidenceChip field={aiField(s)} />
        </span>
      </span>
    );
  return (
    <span className="inline-flex flex-col gap-0.5">
      <span className="truncate">{s.action === 'exclude' ? 'Exclude' : s.accountName}</span>
      <Badge variant="secondary" className="max-w-full truncate">
        Rule: {s.ruleName}
      </Badge>
    </span>
  );
}

/** An AI suggestion as the confidence chip's field (value, confidence, source, alternatives). */
function aiField(s: AiSuggestion) {
  return {
    value: s.accountId,
    confidence: s.confidence,
    source: s.source,
    alternatives: s.alternatives.map((a) => ({
      value: a.accountId,
      label: a.label,
      probability: a.probability,
    })),
  };
}

function ActionCell({
  line,
  tab,
  canEdit,
  busy,
  onOpen,
  onAccept,
  onUndo,
}: {
  line: StatementLine;
  tab: LineStatus;
  canEdit: boolean;
  busy: boolean;
  onOpen: () => void;
  onAccept: () => void;
  onUndo: () => void;
}) {
  const view = line.journalEntryId ? (
    <Button size="sm" variant="link" className="px-2" asChild>
      <Link to="/accounting/journal-entries/$entryId" params={{ entryId: line.journalEntryId }}>
        View
      </Link>
    </Button>
  ) : null;
  if (!canEdit) return view;
  if (tab === 'for_review') {
    const s = line.suggestion;
    if (s?.kind === 'match')
      return (
        <Button size="sm" variant="outline" disabled={busy} onClick={onAccept}>
          Match
        </Button>
      );
    if (s?.kind === 'rule')
      return (
        <Button size="sm" variant="outline" disabled={busy} onClick={onAccept}>
          {s.action === 'exclude' ? 'Exclude' : 'Add'}
        </Button>
      );
    if (s?.kind === 'ai' && s.confidence >= AI_ACCEPT_CONFIDENCE)
      return (
        <Button size="sm" variant="outline" disabled={busy} onClick={onAccept}>
          {s.action === 'transfer' ? 'Transfer' : 'Add'}
        </Button>
      );
    return (
      <Button size="sm" variant="outline" onClick={onOpen}>
        Categorise
      </Button>
    );
  }
  return (
    <span className="inline-flex items-center">
      {view}
      {line.reconciled ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button size="sm" variant="outline" disabled>
                Undo
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>Reconciled. Undo the reconciliation first.</TooltipContent>
        </Tooltip>
      ) : (
        <Button size="sm" variant="outline" disabled={busy} onClick={onUndo}>
          Undo
        </Button>
      )}
    </span>
  );
}
